import { ConvertHelper } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { Observable, of } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import {
  areKeysEqual,
  ISettingInfo,
  IWritableSettings,
  SettingInfo,
  SettingsCatalogueEntry,
  SettingsCatalogueKey,
  SettingsCatalogueValue,
} from './models/settings-catalogue';

export abstract class LocalSettingsService<
  TSettings,
  TSettingsInternal extends TSettings & IWritableSettings<TSettings>,
  TSettingValue extends number | string = number | string,
  TSettingEnum extends Record<Extract<keyof TSettingEnum, string>, TSettingValue> = any
> {
  constructor(
    protected abcConfigService: AbcConfigService,
    protected internalSettingsType: new () => TSettingsInternal,
    protected settingEnumType: TSettingEnum
  ) {}

  private defaultSettingsInfos: Map<TSettingEnum, ISettingInfo>;

  protected abstract get settingsCatalogueStorageKeyName(): string;

  protected buildSettingsCatalogueKey(): Observable<SettingsCatalogueKey> {
    // NOTE for overriders: If Value is empty, don't set it at all, as ist is not serialized and search might fail later
    const key = {
      WebApi: this.abcConfigService.configuration.WebApi.BaseURL,
    };

    return of(key);
  }

  protected get settingInfos(): ReadonlyMap<TSettingEnum, ISettingInfo> {
    // Can be overridden to support differing property names and other return types than string
    if (!this.defaultSettingsInfos) {
      const infos = (this.internalSettingsType as any)?.Infos as Map<TSettingEnum, ISettingInfo>;
      this.defaultSettingsInfos =
        infos ??
        new Map<TSettingEnum, ISettingInfo>(
          ConvertHelper.getEnumEntries(this.settingEnumType).map((e) => [
            (<unknown>e.Value) as TSettingEnum,
            (<unknown>{
              settingName: e.Key,
              responsePropertyName: e.Key.charAt(0).toUpperCase() + e.Key.slice(1),
            }) as ISettingInfo,
          ])
        );
    }
    return this.defaultSettingsInfos;
  }

  private convertFromCatalogueValue(catalogueValue: SettingsCatalogueValue): Observable<TSettings> {
    let internalSettings: TSettingsInternal = null;

    if (catalogueValue) {
      internalSettings = new this.internalSettingsType();

      for (const settingInfo of Array.from(this.settingInfos.values())) {
        const safeSettingInfo: SettingInfo = new SettingInfo(settingInfo);
        const value: unknown = catalogueValue[safeSettingInfo.settingName];

        if (!value && safeSettingInfo?.isOptional === false) {
          internalSettings = null;
          break;
        }

        const settingValue = this.getValueFromStorageSettingValue(safeSettingInfo, value);
        internalSettings[safeSettingInfo.settingName] = settingValue;
      }
    }

    return this.postProcessSettingsAfterRead(internalSettings).pipe(
      map((processedSettings) => <TSettings>(<unknown>processedSettings))
    );
  }

  private convertToCatalogueValue(settings: TSettings): Observable<SettingsCatalogueValue> {
    let catalogueValue: SettingsCatalogueValue = null;
    let internalSettings: TSettingsInternal = null;

    if (settings) {
      internalSettings = new this.internalSettingsType();
      internalSettings.load(settings);
    }

    return this.preProcessSettingsBeforeSave(internalSettings).pipe(
      map((processedSettings) => {
        if (processedSettings) {
          catalogueValue = {};

          for (const settingInfo of Array.from(this.settingInfos.values())) {
            const safeSettingInfo: SettingInfo = new SettingInfo(settingInfo);
            let settingValue: unknown = processedSettings[safeSettingInfo.settingName];
            catalogueValue[safeSettingInfo.settingName] = this.getStorageSettingValueFromValue(
              safeSettingInfo,
              settingValue
            );
          }
        }

        return catalogueValue;
      })
    );
  }

  protected postProcessSettingsAfterRead(settings: TSettingsInternal): Observable<TSettingsInternal> {
    // Can be used to modify settings or set them to null if they are invalid
    return of(settings);
  }

  protected preProcessSettingsBeforeSave(settings: TSettingsInternal): Observable<TSettingsInternal> {
    // Can be used to modify settings or set them to null to prevent save
    return of(settings);
  }

  protected createSettingsFromResponseItem(responseItem: any): TSettings {
    let settings: TSettings = null;

    if (responseItem) {
      settings = new this.internalSettingsType();

      // TODO: With --downlevelIteration compiler option:
      // for (let settingInfo of this.settingInfos.values()) {
      for (const settingInfo of Array.from(this.settingInfos.values())) {
        const safeSettingInfo: SettingInfo = new SettingInfo(settingInfo);
        const responseValue: unknown = responseItem[safeSettingInfo.responsePropertyName];

        if (!responseValue && !safeSettingInfo.isOptional)
          throw new Error(
            `Incomplete Login Response. Parameter '${safeSettingInfo.responsePropertyName}' was returned empty.`
          );

        settings[safeSettingInfo.settingName] = this.getValueFromStorageSettingValue(safeSettingInfo, responseValue);
      }
    }

    return <TSettings>settings;
  }

  protected readSettingsFromStorage(specificStorageOnly?: Storage): Observable<TSettings> {
    return this.buildSettingsCatalogueKey().pipe(
      concatMap((catalogueKey) => {
        const catalogueStorageKeyName: string = this.settingsCatalogueStorageKeyName;
        let catalogueValue: SettingsCatalogueValue = null;

        let readFromLocalStorage = false;
        if (catalogueKey) {
          const storages: Storage[] = specificStorageOnly ? [specificStorageOnly] : [sessionStorage, localStorage];

          for (const storage of storages) {
            const catalogueJSONText: string = storage.getItem(catalogueStorageKeyName);

            if (catalogueJSONText) {
              const catalogueJSONArray: SettingsCatalogueEntry[] = JSON.parse(catalogueJSONText);
              const catalogueEntry = catalogueJSONArray.find((e) => areKeysEqual(e.Key, catalogueKey));

              if (catalogueEntry) {
                catalogueValue = catalogueEntry.Value;
                readFromLocalStorage = storage === localStorage;
                break;
              }
            }
          }
        }

        return this.convertFromCatalogueValue(catalogueValue).pipe(
          tap((settings) => {
            if (!specificStorageOnly && readFromLocalStorage) {
              this.writeSettingsToStorage(settings, sessionStorage).subscribe();
            }
          })
        );
      })
    );
  }

  protected writeSettingsToStorage(settings: TSettings, specificStorageOnly?: Storage): Observable<void> {
    return this.buildSettingsCatalogueKey().pipe(
      concatMap((catalogueKey) => {
        if (catalogueKey) {
          const catalogueStorageKeyName: string = this.settingsCatalogueStorageKeyName;
          return this.convertToCatalogueValue(settings).pipe(
            map((catalogueValue) => {
              const storages: Storage[] = specificStorageOnly ? [specificStorageOnly] : [sessionStorage, localStorage];

              for (const storage of storages) {
                let catalogueJSONArray: SettingsCatalogueEntry[] = null;

                let catalogueJSONText: string = storage.getItem(catalogueStorageKeyName);

                if (catalogueJSONText) {
                  catalogueJSONArray = JSON.parse(catalogueJSONText);
                } else if (catalogueValue) {
                  catalogueJSONArray = [];
                }

                if (catalogueJSONArray) {
                  let mustSaveCatalogue: boolean;

                  if (catalogueJSONArray.some((e) => areKeysEqual(e.Key, catalogueKey))) {
                    catalogueJSONArray = catalogueJSONArray.filter((e) => !areKeysEqual(e.Key, catalogueKey));
                    mustSaveCatalogue = true;
                  } else if (catalogueValue) {
                    mustSaveCatalogue = true;
                  } else {
                    mustSaveCatalogue = false;
                  }

                  if (catalogueValue) {
                    const catalogueEntry: SettingsCatalogueEntry = {
                      Key: catalogueKey,
                      Value: catalogueValue,
                    };
                    catalogueJSONArray.push(catalogueEntry);
                  }

                  if (mustSaveCatalogue) {
                    if (catalogueJSONArray.length === 0) {
                      storage.removeItem(catalogueStorageKeyName);
                    } else {
                      catalogueJSONText = JSON.stringify(catalogueJSONArray);
                      storage.setItem(catalogueStorageKeyName, catalogueJSONText);
                    }
                  }
                }
              }
            })
          );
        }
      })
    );
  }

  private getValueFromStorageSettingValue(safeSettingInfo: SettingInfo, settingValue: unknown): unknown {
    if (safeSettingInfo?.settingValueType === Object) {
      if (typeof settingValue === 'string') {
        return safeSettingInfo.getSettingValueFromText(settingValue);
      } else {
        return settingValue;
      }
    } else {
      if (typeof settingValue === 'string') {
        return safeSettingInfo.getSettingValueFromText(settingValue);
      } else {
        return safeSettingInfo.getSettingValueFromText(safeSettingInfo.getTextFromSettingValue(settingValue));
      }
    }
  }

  private getStorageSettingValueFromValue(safeSettingInfo: SettingInfo, value: unknown): unknown {
    if (safeSettingInfo?.settingValueType === Object) {
      return value;
    } else {
      return safeSettingInfo.getTextFromSettingValue(value);
    }
  }
}
